import { Chip } from "@material-ui/core";
import { Datagrid } from "components/CustomizableDataGrid";
import { AppList } from "components/list/AppList";
import { ScrollingWrapper } from "components/ScrollingWrapper";
import { useGridUserDataGlobalFiltersContext } from "contexts/GridUserDataGlobalFiltersProvider";
import { useTerminalsContext } from "contexts/TerminalsProvider";
import React from "react";
import {
	BooleanField,
	EditButton,
	FunctionField,
	SelectField,
	TextField,
} from "react-admin";
import { useAsyncColumnsStorage } from "utils/hooks/useAsyncColumnsStorage";

import { useEnumSelectChoices } from "../../../components/_helpers/useEnumSelectChoices";
import { DateTimeField } from "../../../components/fields/DateFormatField";
import { FakeReferenceTextField } from "../../../components/fields/FakeReferenceTextField";
import { UserFullNameReferenceField } from "../../../components/fields/UserFullNameReferenceField";
import { LinkOrderType } from "../../../entities/LinkOrderType";
import { OrderGroupCargoType } from "../../../entities/OrderGroupCargoType";
import { OrderGroupState } from "../../../entities/OrderGroupState";
import { useOutDocStatuses } from "../../../utils/hooks/useOutDocStatuses";
import { AddOrderToGroupButton } from "../components/AddOrderToGroupButton";
import { ConfirmedByField } from "../components/ConfirmedByField";
import { OrderGroupsFilter } from "./ListFilters";

const defaultSort = { field: "created_at", order: "DESC" };

export const OrderGroupsList = (props) => {
	const cargoTypeChoices = useEnumSelectChoices(OrderGroupCargoType, "order-group-cargo-type");
	const { choices: siStatusChoices } = useOutDocStatuses();
	const linkOrderTypeChoices = useEnumSelectChoices(LinkOrderType, "link-order-type");
	const { data: terminals = {} } = useTerminalsContext();
	const { data: globalFilters = {}, isLoading } = useGridUserDataGlobalFiltersContext();
	const columnsAsyncStorage = useAsyncColumnsStorage("order-groups-columns");

	if (isLoading || !columnsAsyncStorage.isLoaded) return null;

	const currentFilters = { terminal_id: globalFilters?.terminal_id?.[0] };

	return (
		<AppList
			{...props}
			sort={defaultSort}
			exporter={false}
			bulkActionButtons={false}
			filters={<OrderGroupsFilter />}
			filterDefaultValues={{ ...currentFilters, group_state: OrderGroupState.all }}
			filter={currentFilters}
		>
			<ScrollingWrapper>
				<Datagrid storage={columnsAsyncStorage} reorderingSupport={true}>
					<EditButton label="" />
					<AddOrderToGroupButton label="" />
					<TextField source="id" />
					<SelectField source="cargo_type" choices={cargoTypeChoices} />
					<FunctionField
						source="orders"
						sortable={false}
						render={(record) =>
							(record.orders || []).map((order) => (
								<Chip
									key={order.order_id}
									size="small"
									clickable
									component="a"
									href={`#/orders/${order.order_id}/show`}
									target="_blank"
									rel="noopener noreferrer"
									label={order.reference || order.order_id}
									style={{ marginRight: 4 }}
									onClick={(event) => event.stopPropagation()}
								/>
							))
						}
					/>
					<FunctionField
						source="task_links"
						sortable={false}
						render={(record) =>
							(record.task_links || []).map((taskLink) => (
								<Chip
									key={taskLink.id}
									size="small"
									clickable
									component="a"
									href={`#/tasks/${taskLink.id}`}
									target="_blank"
									rel="noopener noreferrer"
									label={taskLink.title || taskLink.id}
									style={{ marginRight: 4 }}
									onClick={(event) => event.stopPropagation()}
								/>
							))
						}
					/>
					<BooleanField source="is_ready_to_process" sortable={false} />
					<ConfirmedByField source="confirmed_by" sortable={false} />
					<UserFullNameReferenceField source="task_taken_by" sortable={false} />
					<UserFullNameReferenceField source="task_on_hold_assignee_id" sortable={false} />
					<TextField source="rank" />
					<SelectField source="si_status" choices={siStatusChoices} />
					<SelectField source="link_order_type" choices={linkOrderTypeChoices} />
					<TextField source="truck" />
					<TextField source="trailer" />
					<TextField source="trip_number" />
					<FakeReferenceTextField source="port" replaceSource="port_name" sortable={false} />
					<FunctionField
						source="terminal_id"
						sortable={false}
						render={(record) => terminals[record.terminal_id]?.name}
					/>
					<DateTimeField source="created_at" />
					<DateTimeField source="updated_at" />
					<UserFullNameReferenceField source="updated_by" sortable={false} />
				</Datagrid>
			</ScrollingWrapper>
		</AppList>
	);
};
